import { DatevBookingData } from '../../types';

// UI-level booking type allows extra frontend-only fields in addition to API fields
export type ExtendedDatevBookingData = DatevBookingData & {
  [extraField: string]: string | number | null;
};

export type ColumnKey = keyof ExtendedDatevBookingData;

export type ColumnType = 'currency' | 'number' | 'text' | 'select' | 'readonly';

export interface ColumnConfig {
  key: ColumnKey;
  label: string;
  type: ColumnType;
  nullable?: boolean;
  readOnly?: boolean;
  hiddenByDefault?: boolean;
  defaultValue: string | number | null;
  selectOptions?: { value: string; label: string }[];
  step?: string;
}

// Base column config for fields coming from the API
export const COLUMN_CONFIG_MAP: Record<string, ColumnConfig> = {
  amount: {
    key: 'amount',
    label: 'Betrag',
    type: 'currency',
    defaultValue: 0,
    step: '0.01',
  },
  debitCreditFlag: {
    key: 'debitCreditFlag',
    label: 'S/H',
    type: 'select',
    defaultValue: 'S',
    selectOptions: [
      { value: 'S', label: 'S (Soll)' },
      { value: 'H', label: 'H (Haben)' },
    ],
  },
  accountNumber: {
    key: 'accountNumber',
    label: 'Konto',
    type: 'text',
    defaultValue: '',
  },
  contraAccountNumber: {
    key: 'contraAccountNumber',
    label: 'Gegenkonto',
    type: 'text',
    defaultValue: null,
    nullable: true,
  },
  taxKey: {
    key: 'taxKey',
    label: 'BU-Schlüssel',
    type: 'number',
    defaultValue: 9,
    step: '1',
  },
  invoiceDate: {
    key: 'invoiceDate',
    label: 'Belegdatum',
    type: 'text',
    defaultValue: '',
  },
  invoiceNumber: {
    key: 'invoiceNumber',
    label: 'Belegfeld 1',
    type: 'text',
    defaultValue: '',
  },
  bookingText: {
    key: 'bookingText',
    label: 'Buchungstext',
    type: 'text',
    defaultValue: '',
  },
  sourceIndicator: {
    key: 'sourceIndicator',
    label: 'Herkunft-Kz',
    type: 'readonly',
    readOnly: true,
    hiddenByDefault: true,
    defaultValue: 'RE',
  },
  postingGuid: {
    key: 'postingGuid',
    label: 'Buchungs GUID',
    type: 'text',
    defaultValue: null,
    nullable: true,
    hiddenByDefault: true,
  },
};

// Labels for frontend-only hidden fields (not returned by the API)
const hiddenFields = [
  'WKZ Umsatz',
  'Kurs',
  'Basis-Umsatz',
  'WKZ Basis-Umsatz',
  'Belegfeld 2',
  'Skonto',
  'Postensperre',
  'Diverse Adressnummer',
  'Geschäftspartnerbank',
  'Sachverhalt',
  'Zinssperre',
  'Beleglink',
  'Beleginfo - Art 1',
  'Beleginfo - Inhalt 1',
  'Beleginfo - Art 2',
  'Beleginfo - Inhalt 2',
  'Beleginfo - Art 3',
  'Beleginfo - Inhalt 3',
  'Beleginfo - Art 4',
  'Beleginfo - Inhalt 4',
  'Beleginfo - Art 5',
  'Beleginfo - Inhalt 5',
  'Beleginfo - Art 6',
  'Beleginfo - Inhalt 6',
  'Beleginfo - Art 7',
  'Beleginfo - Inhalt 7',
  'Beleginfo - Art 8',
  'Beleginfo - Inhalt 8',
  'KOST1 - Kostenstelle',
  'KOST2 - Kostenstelle',
  'Kost-Menge',
  'EU-Land u. UStID (Bestimmung)',
  'EU-Steuersatz (Bestimmung)',
  'Abw. Versteuerungsart',
  'Sachverhalt L+L',
  'Funktionsergänzung L+L',
  'BU 49 Hauptfunktionstyp',
  'BU 49 Hauptfunktionsnummer',
  'BU 49 Funktionsergänzung',
  'Zusatzinformation - Art 1',
  'Zusatzinformation- Inhalt 1',
  'Zusatzinformation - Art 2',
  'Zusatzinformation- Inhalt 2',
  'Zusatzinformation - Art 3',
  'Zusatzinformation- Inhalt 3',
  'Zusatzinformation - Art 4',
  'Zusatzinformation- Inhalt 4',
  'Zusatzinformation - Art 5',
  'Zusatzinformation- Inhalt 5',
  'Zusatzinformation - Art 6',
  'Zusatzinformation- Inhalt 6',
  'Zusatzinformation - Art 7',
  'Zusatzinformation- Inhalt 7',
  'Zusatzinformation - Art 8',
  'Zusatzinformation- Inhalt 8',
  'Zusatzinformation - Art 9',
  'Zusatzinformation- Inhalt 9',
  'Zusatzinformation - Art 10',
  'Zusatzinformation- Inhalt 10',
  'Zusatzinformation - Art 11',
  'Zusatzinformation- Inhalt 11',
  'Zusatzinformation - Art 12',
  'Zusatzinformation- Inhalt 12',
  'Zusatzinformation - Art 13',
  'Zusatzinformation- Inhalt 13',
  'Zusatzinformation - Art 14',
  'Zusatzinformation- Inhalt 14',
  'Zusatzinformation - Art 15',
  'Zusatzinformation- Inhalt 15',
  'Zusatzinformation - Art 16',
  'Zusatzinformation- Inhalt 16',
  'Zusatzinformation - Art 17',
  'Zusatzinformation- Inhalt 17',
  'Zusatzinformation - Art 18',
  'Zusatzinformation- Inhalt 18',
  'Zusatzinformation - Art 19',
  'Zusatzinformation- Inhalt 19',
  'Zusatzinformation - Art 20',
  'Zusatzinformation- Inhalt 20',
  'Stück',
  'Gewicht',
  'Zahlweise',
  'Forderungsart',
  'Veranlagungsjahr',
  'Zugeordnete Fälligkeit',
  'Skontotyp',
  'Auftragsnummer',
  'Buchungstyp (Anzahlungen)',
  'USt-Schlüssel (Anzahlungen)',
  'EU-Land (Anzahlungen)',
  'Sachverhalt L+L (Anzahlungen)',
  'EU-Steuersatz (Anzahlungen)',
  'Erlöskonto (Anzahlungen)',
  'KOST-Datum',
  'SEPA-Mandatsreferenz',
  'Skontosperre',
  'Gesellschaftername',
  'Beteiligtennummer',
  'Identifikationsnummer',
  'Zeichnernummer',
  'Postensperre bis',
  'Bezeichnung SoBil-Sachverhalt',
  'Kennzeichen SoBil-Buchung',
  'Festschreibung',
  'Leistungsdatum',
  'Datum Zuord. Steuerperiode',
  'Fälligkeit',
  'Generalumkehr (GU)',
  'Steuersatz',
  'Land',
  'Abrechnungsreferenz',
  'BVV-Position',
  'EU-Land u. UStID (Ursprung)',
  'EU-Steuersatz (Ursprung)',
  'Abw. Skontokonto',
];

// Column configs for frontend-only hidden fields
const EXTRA_COLUMN_CONFIGS: ColumnConfig[] = hiddenFields.map(label => ({
  key: label,
  label,
  type: 'text',
  defaultValue: '',
  hiddenByDefault: true,
}));

export const COLUMN_CONFIGS: ColumnConfig[] = [
  ...Object.values(COLUMN_CONFIG_MAP),
  ...EXTRA_COLUMN_CONFIGS,
];

export const ALL_COLUMNS: ColumnKey[] = COLUMN_CONFIGS.map(config => config.key);

export const HIDDEN_COLUMNS: ColumnKey[] = COLUMN_CONFIGS.filter(
  config => config.hiddenByDefault
).map(config => config.key);

// Create a new empty entry with all columns initialized to their defaultValue
export const buildEmptyEntry = (): ExtendedDatevBookingData => {
  const acc: ExtendedDatevBookingData = {} as ExtendedDatevBookingData;
  COLUMN_CONFIGS.forEach(config => {
    acc[config.key] = config.defaultValue;
  });
  return acc;
};

// Normalize API booking rows so all frontend-only fields exist with default values
export const initializeUiBookingRows = (
  rows: DatevBookingData[]
): ExtendedDatevBookingData[] => {
  return rows.map(row => {
    const uiRow: ExtendedDatevBookingData = { ...row } as ExtendedDatevBookingData;
    COLUMN_CONFIGS.forEach(config => {
      if (uiRow[config.key] === undefined || uiRow[config.key] === null) {
        uiRow[config.key] = config.defaultValue;
      }
    });
    return uiRow;
  });
};
